import classNames from "classnames";
import { ColumnSelector } from "../../components/column-selector";
import { GanttChartColumnMapping, GanttItemMapping, PossibleTypesForFields } from "./synced-gantt-chart.types";
import styles from "./synced-gantt-chart.module.css";
import { MondayColumn } from "shared/integrations/mapper";

const Arrow = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="11" height="6" viewBox="0 0 11 6" fill="none">
    <path d="M8.3457 0.500035L10.3873 2.5415" stroke="#7F869B" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8.3457 4.58057L10.3873 2.5391" stroke="#7F869B" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="0.5" y1="2.54932" x2="9.70818" y2="2.54932" stroke="#7F869B" strokeLinecap="round" />
  </svg>
);

const TIMELINE_AND_DATE_TYPES = [...PossibleTypesForFields.timeline, ...PossibleTypesForFields.date];
const DATE_ONLY_TYPES = [...PossibleTypesForFields.date];

interface MappingSectionProps {
  title: string;
  target: "items" | "subItems";
  columnMapping: GanttChartColumnMapping;
  columns: MondayColumn[];
  getColumnsForField: (types: string[], selectedColumnId: string | null) => MondayColumn[];
  isColumnEnabled: (columnId: string, types: string[]) => boolean;
  handleItemMappingChange: (
    mapTarget: "items" | "subItems",
    field: keyof Omit<GanttItemMapping, "dependencyEnabled">,
    value: string | null
  ) => void;
  usesDateColumns: boolean;
  disabled: boolean;
}

export function MappingSection({
  title,
  target,
  columnMapping,
  columns,
  getColumnsForField,
  isColumnEnabled,
  handleItemMappingChange,
  usesDateColumns,
  disabled,
}: MappingSectionProps) {
  const mapping = columnMapping[target];

  const mainColumnOptions = getColumnsForField(TIMELINE_AND_DATE_TYPES, mapping.timeline ?? mapping.startDate);
  const endDateOptions = getColumnsForField(DATE_ONLY_TYPES, mapping.endDate);

  // Determine the selected value for main dropdown
  const mainSelectedValue = mapping.timeline ?? mapping.startDate;

  const handleMainColumnSelect = (columnId: string) => {
    const column = columns.find((c) => c.id === columnId);
    if (!column) return;

    if (column.type === "timeline") {
      handleItemMappingChange(target, "timeline", columnId);
    } else if (column.type === "date") {
      handleItemMappingChange(target, "startDate", columnId);
    }
  };

  return (
    <div className={styles.mappingRow}>
      <div
        className={classNames(styles.mappingLabel, {
          [styles.mappingLabelActive]: columns.length > 0 && !disabled,
        })}
      >
        {title} <Arrow />
      </div>
      <div className={styles.dateColumnsContainer}>
        <ColumnSelector
          columns={mainColumnOptions}
          selectedColumn={mainSelectedValue}
          onSelect={handleMainColumnSelect}
          placeholder="Choose column"
          disabled={disabled}
          isColumnDisabled={(column) => !!column && !isColumnEnabled(column.id, TIMELINE_AND_DATE_TYPES)}
          disabledColumnTooltip="Supports Timeline or Date columns"
          inlinePrefix={usesDateColumns && mainSelectedValue ? "Start: " : undefined}
        />
        {usesDateColumns && (
          <ColumnSelector
            columns={endDateOptions}
            selectedColumn={mapping.endDate}
            onSelect={(columnId) => handleItemMappingChange(target, "endDate", columnId)}
            placeholder="End date"
            disabled={disabled}
            isColumnDisabled={(column) => !!column && !isColumnEnabled(column.id, DATE_ONLY_TYPES)}
            disabledColumnTooltip="Supports Date columns"
            inlinePrefix={mapping.endDate ? "End: " : undefined}
          />
        )}
      </div>
    </div>
  );
}
